import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, IconButton, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import type { CustomerKycDisplayStatus, CustomerKycStatus, StyleObj } from '../../@types';
import useLocalization from '../../hooks/useLocalization';
import { useSkinStyles } from '../../hooks/useSkinStyles';
import { KYC_VERIFICATION_CARD_SKIN_STYLES } from '../../styles/components/kycVerificationAccountSection';
import { KYC_VERIFICATION_TRIGGER, type KycVerificationTrigger } from '../../types/enums';
import { getCustomerKycDisplayStatus } from '../../utils/kyc';
import { mergeSx } from '../../utils/styles';
import KycLanguageSelect from './KycLanguageSelect';
import KycVerificationActionButton from './KycVerificationActionButton';

const styles = {
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 2,
    minHeight: '100%',
    p: 3,
    borderRadius: 3,
    border: '1px solid',
  },
  header: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1.5,
    textAlign: 'center',
  },
  iconBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '50%',
    flexShrink: 0,
  },
  icon: {
    fontSize: 32,
  },
  titleContent: {
    display: 'grid',
    justifyItems: 'center',
    gap: 0.75,
    minWidth: 0,
  },
  title: {
    color: 'text.primary',
    fontWeight: 700,
  },
  description: {
    lineHeight: 1.5,
    maxWidth: 320,
  },
  statusGroup: {
    display: 'grid',
  },
  statusLabel: {
    typography: 'body3',
    fontWeight: 600,
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    width: 'fit-content',
    px: 1.25,
    py: 0.75,
    borderRadius: 999,
    fontWeight: 600,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  statusText: {
    typography: 'body2',
    fontWeight: 700,
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    color: 'neutral.300',
    '&:hover': {
      color: 'text.primary',
    },
  },
  closeIcon: {
    fontSize: { xs: '1rem', md: '1.125rem' },
  },
} as const;

const getVerificationDescription = (
  t: ReturnType<typeof useLocalization>['t'],
  status: CustomerKycDisplayStatus,
  trigger?: KycVerificationTrigger,
  attemptsExceeded?: boolean
): string => {
  if (attemptsExceeded && status === 'failed') {
    return t(
      'kyc.maxAttemptsReachedMessage',
      "You've reached the maximum number of verification attempts. Our team will review your case and contact you if more information is needed."
    );
  }

  if (status === 'failed') {
    return t(
      'kyc.verificationFailedMessage',
      "We couldn't verify your identity with the information provided. Please check your details and try again or please contact Support for more information."
    );
  }

  if (status === 'verified') {
    return t('kyc.verifiedMessage', 'Your account is verified and can be used without restrictions.');
  }

  if (status === 'inProgress') {
    return t(
      'kyc.inProgressMessage',
      'Your verification is in progress. Return to the verification flow if you need to complete it.'
    );
  }

  if (status === 'pending') {
    return t('kyc.pendingMessage', 'Your verification is complete and awaiting approval.');
  }

  switch (trigger) {
    case KYC_VERIFICATION_TRIGGER.REGISTRATION:
      return t('kyc.registrationMessage', 'Complete verification to continue using your account.');
    case KYC_VERIFICATION_TRIGGER.FIRST_DEPOSIT:
      return t('kyc.firstDepositMessage', 'Complete verification before making your first deposit.');
    case KYC_VERIFICATION_TRIGGER.FIRST_WITHDRAWAL:
      return t('kyc.firstWithdrawalMessage', 'Complete verification before making your first withdrawal.');
    default:
      return t('kyc.defaultMessage', 'Complete verification to continue.');
  }
};

const getVerificationTitle = (
  t: ReturnType<typeof useLocalization>['t'],
  status: CustomerKycDisplayStatus,
  attemptsExceeded?: boolean
): string => {
  if (attemptsExceeded && status === 'failed') {
    return t('kyc.maxAttemptsReachedTitle', 'Verification attempts exceeded');
  }

  if (status === 'failed') {
    return t('kyc.failedTitle', 'Verification failed');
  }

  if (status === 'verified') {
    return t('kyc.verifiedTitle', 'Account verified');
  }

  if (status === 'inProgress') {
    return t('kyc.inProgressTitle', 'Verification in progress');
  }

  if (status === 'pending') {
    return t('kyc.pendingTitle', 'Verification pending');
  }

  return t('kyc.verificationRequiredTitle', 'Verification required');
};

const getVerificationStatusLabel = (
  t: ReturnType<typeof useLocalization>['t'],
  status: CustomerKycDisplayStatus
): string => {
  if (status === 'verified') {
    return t('kyc.statusVerified', 'Verified');
  }

  if (status === 'inProgress') {
    return t('kyc.statusInProgress', 'In Progress');
  }

  if (status === 'pending') {
    return t('kyc.statusPending', 'Pending');
  }

  if (status === 'failed') {
    return t('kyc.statusFailed', 'Failed');
  }

  return t('kyc.statusNotVerified', 'Not Verified');
};

const getVerificationActionLabel = (
  t: ReturnType<typeof useLocalization>['t'],
  status: CustomerKycDisplayStatus
): string => {
  if (status === 'inProgress') {
    return t('kyc.continueVerification', 'Continue Verification');
  }

  if (status === 'failed') {
    return t('kyc.retryVerification', 'Try Again');
  }

  return t('kyc.startVerification', 'Start Verification');
};

const getVerificationStatusColor = (status: CustomerKycDisplayStatus): string => {
  if (status === 'verified') {
    return 'success.main';
  }

  if (status === 'inProgress' || status === 'pending') {
    return 'warning.main';
  }

  return 'error.main';
};

export type KycVerificationCardProps = {
  allowLanguageSelection?: boolean;
  attemptsExceeded?: boolean;
  isPending?: boolean;
  onClose?: () => void;
  onStartVerification?: () => void;
  status?: CustomerKycStatus;
  sx?: StyleObj['root'];
  testId?: string;
  title?: ReactNode;
  trigger?: KycVerificationTrigger;
};

const KycVerificationCard = ({
  allowLanguageSelection = false,
  attemptsExceeded = false,
  isPending = false,
  onClose,
  onStartVerification,
  status,
  sx,
  testId,
  title,
  trigger,
}: KycVerificationCardProps) => {
  const { t } = useLocalization();
  const skinStyles = useSkinStyles(KYC_VERIFICATION_CARD_SKIN_STYLES);
  const displayStatus = getCustomerKycDisplayStatus(status);
  const hasReachedFailedAttemptLimit = attemptsExceeded && displayStatus === 'failed';
  const canStartVerification = displayStatus !== 'verified' && displayStatus !== 'pending';
  const actionLabel = getVerificationActionLabel(t, displayStatus);
  const statusColor = getVerificationStatusColor(displayStatus);
  const statusDotSx = { ...styles.statusDot, backgroundColor: statusColor };

  return (
    <Box sx={mergeSx(styles.root, skinStyles.root, sx)} data-testid={testId}>
      <Box sx={mergeSx(styles.header, skinStyles.header)}>
        {allowLanguageSelection && <KycLanguageSelect />}
        <Box sx={mergeSx(styles.iconBadge, skinStyles.iconBadge)}>
          <LockOutlinedIcon sx={styles.icon} />
        </Box>
        <Box sx={styles.titleContent}>
          <Typography variant='h3' sx={mergeSx(styles.title, skinStyles.title)}>
            {title ?? getVerificationTitle(t, displayStatus, hasReachedFailedAttemptLimit)}
          </Typography>
          <Typography variant='body3' sx={mergeSx(styles.description, skinStyles.description)}>
            {getVerificationDescription(t, displayStatus, trigger, hasReachedFailedAttemptLimit)}
          </Typography>
        </Box>
        {onClose && (
          <IconButton aria-label={t('close')} onClick={onClose} sx={styles.closeButton}>
            <CloseIcon sx={styles.closeIcon} />
          </IconButton>
        )}
      </Box>
      <Box sx={mergeSx(styles.statusGroup, skinStyles.statusGroup)}>
        <Typography sx={mergeSx(styles.statusLabel, skinStyles.statusLabel)}>
          {t('kyc.statusLabel', 'Status')}
        </Typography>
        <Box sx={mergeSx(styles.statusChip, skinStyles.statusChip)}>
          <Box sx={statusDotSx} />
          <Typography component='span' sx={mergeSx(styles.statusText, skinStyles.statusText)}>
            {getVerificationStatusLabel(t, displayStatus)}
          </Typography>
        </Box>
      </Box>
      {canStartVerification && !attemptsExceeded && onStartVerification && (
        <KycVerificationActionButton disabled={isPending} onClick={onStartVerification} label={actionLabel} />
      )}
    </Box>
  );
};

export default KycVerificationCard;
